'use client'

import Link from 'next/link'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useProductByIdQuery } from '@/generated/graphql'
import { Button } from '@/General/Button'
import { Price } from '@/General/Price'
import { useAddItemToCart } from '@/useAddItemToCart'
import { toastEmitter } from '@/General/Toast'
import {
  getCustomAddToCartFlowMetadata,
  type CustomAddToCartFlowProps,
} from '@/lib/config/customAddToCartFlows'
import { HYBRID_CUP_INCLUDED_SLEEVE_DESCRIPTION } from '@/lib/config/hybridCupBundle'
import {
  hybridCupCupSwatchUrl,
  hybridCupSleeveSwatchUrl,
  hybridCupSolidCupPromoImageUrl,
} from '@/lib/config/hybridCupSwatchImages'
import { useStorefrontMarket } from '@/useStorefrontMarket'

/** Must match catalogue / seeder (Hybrid Cup bundle). */
const SLEEVES_PRODUCT_ID = 29944
const SOLID_CUP_PRODUCT_ID = 29945

const STEP = { CUP: 0, SLEEVES: 1, EXTRA: 2 } as const
type StepIndex = (typeof STEP)[keyof typeof STEP]

type AddProgress = {
  current: number
  total: number
  label: string
}

function productPageHrefFromApiUrl(
  url: string | null | undefined
): string | null {
  if (!url?.trim()) return null
  return `/${url.replace(/^\/+|\/+$/g, '')}`
}

function BackToProductLink({
  productUrl,
  variant,
}: {
  productUrl: string | null | undefined
  variant: 'header' | 'muted'
}) {
  const href = productPageHrefFromApiUrl(productUrl)
  if (!href) return null
  const className =
    variant === 'header'
      ? 'inline-block text-sm font-semibold text-white/90 hover:text-white mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-vi-gray rounded-sm'
      : 'inline-block text-sm font-semibold text-gray-700 hover:text-gray-900 mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-vi-red focus-visible:ring-offset-2 rounded-sm'
  return (
    <Link href={href} className={className}>
      ← Back to product
    </Link>
  )
}

type Option = {
  id: number | null | undefined
  description: string | null | undefined
  position: number
  price: number
  salePrice?: number | null
  inStock: boolean
  currency?: { symbol?: string | null } | null
}

function getProductIdFromSearchParams(
  searchParams: Record<string, string | string[] | undefined>
): number | null {
  const raw = searchParams?.productId
  if (raw == null) return null
  const single = Array.isArray(raw) ? raw[0] : raw
  const n = parseInt(single, 10)
  return Number.isFinite(n) ? n : null
}

function effectiveOptionPrice(o: Option): number {
  const p = o.price
  const s = o.salePrice
  if (s != null && s < p) return s
  return p
}

function isSelectableOption(option: Option): option is Option & { id: number } {
  return option.inStock && option.id != null
}

type ConfiguratorTileProps = {
  label: string
  imageUrl: string | null
  selected: boolean
  currencySymbol: string
  price: number
  salePrice?: number | null
  showPricing?: boolean
  imageSize?: string
  disabled?: boolean
  onSelect: () => void
}

function ConfiguratorTile({
  label,
  imageUrl,
  selected,
  currencySymbol,
  price,
  salePrice,
  showPricing = true,
  imageSize = 'cover',
  disabled = false,
  onSelect,
}: ConfiguratorTileProps) {
  const displayPrice =
    salePrice != null && salePrice < price ? salePrice : price
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={label}
      className={[
        'relative aspect-square min-h-[100px] w-full rounded border-2 overflow-hidden text-left transition',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-vi-red focus-visible:ring-offset-2',
        disabled
          ? 'border-gray-200 cursor-not-allowed opacity-50'
          : selected
            ? 'border-vi-red ring-2 ring-vi-red ring-offset-2'
            : 'border-gray-300 hover:border-gray-400',
        imageUrl
          ? 'bg-white'
          : 'bg-gray-50 flex flex-col items-center justify-center p-3',
      ].join(' ')}
      style={
        imageUrl
          ? {
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: imageSize,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    >
      {imageUrl ? (
        <>
          <span
            className="absolute top-2 left-2 right-2 font-semibold text-gray-900 leading-tight"
            style={{
              fontSize: '0.7em',
              textShadow: '0 0 2px #fff, 0 0 6px #fff',
            }}
          >
            {label}
          </span>
          {showPricing ? (
            <div className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-2 bg-white/90 px-2 py-1.5 text-xs font-semibold text-gray-900 tabular-nums border-t border-gray-200/90 backdrop-blur-[2px]">
              <span>
                {currencySymbol}
                {Number(displayPrice).toFixed(2)}
              </span>
              {disabled ? (
                <span className="text-[0.65rem] uppercase tracking-wide text-gray-600">
                  Out of stock
                </span>
              ) : null}
            </div>
          ) : disabled ? (
            <span className="absolute bottom-0 inset-x-0 bg-white/90 px-2 py-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-gray-600 border-t border-gray-200/90 backdrop-blur-[2px]">
              Out of stock
            </span>
          ) : null}
        </>
      ) : (
        <>
          <span className="font-medium text-center text-sm text-gray-800 block">
            {label}
          </span>
          {showPricing ? (
            <Price
              price={price}
              salePrice={salePrice}
              currencySymbol={currencySymbol}
              size="sm"
            />
          ) : null}
          {disabled ? (
            <span className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
              Out of stock
            </span>
          ) : null}
        </>
      )}
    </button>
  )
}

type SleeveQuantityTileProps = {
  label: string
  imageUrl: string | null
  quantity: number
  disabled?: boolean
  onIncrement: () => void
  onDecrement: () => void
}

function SleeveQuantityTile({
  label,
  imageUrl,
  quantity,
  disabled = false,
  onIncrement,
  onDecrement,
}: SleeveQuantityTileProps) {
  const selected = quantity > 0
  const chooseOneFromEmptyTile = !disabled && quantity === 0
  return (
    <div
      className={[
        'relative aspect-[4/5] min-h-[154px] w-full rounded border-2 overflow-hidden text-left transition sm:min-h-[168px] md:min-h-[184px]',
        disabled
          ? 'border-gray-200 opacity-50'
          : selected
            ? 'border-vi-red ring-2 ring-vi-red ring-offset-2'
            : 'border-gray-300 hover:border-gray-400',
        imageUrl ? 'bg-white' : 'bg-gray-50',
      ].join(' ')}
      style={
        imageUrl
          ? {
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: 'auto 72%',
              backgroundPosition: 'center 42%',
              backgroundRepeat: 'no-repeat',
            }
          : undefined
      }
    >
      {chooseOneFromEmptyTile ? (
        <button
          type="button"
          aria-label={`Add one ${label} sleeve`}
          onClick={onIncrement}
          className="absolute inset-0 z-10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-vi-red focus-visible:ring-inset"
        />
      ) : null}
      <span
        className="pointer-events-none absolute top-2 left-2 right-2 z-20 font-semibold text-gray-900 leading-tight"
        style={{
          fontSize: '0.7em',
          textShadow: '0 0 2px #fff, 0 0 6px #fff',
        }}
      >
        {label}
      </span>
      {disabled ? (
        <span className="absolute bottom-0 inset-x-0 bg-white/90 px-2 py-1.5 text-[0.65rem] font-semibold uppercase tracking-wide text-gray-600 border-t border-gray-200/90 backdrop-blur-[2px]">
          Out of stock
        </span>
      ) : (
        <div
          onClick={chooseOneFromEmptyTile ? onIncrement : undefined}
          className={[
            'absolute bottom-0 inset-x-0 z-30 flex items-center justify-between gap-2 bg-white/90 px-2 py-1.5 text-sm font-semibold text-gray-900 border-t border-gray-200/90 backdrop-blur-[2px]',
            chooseOneFromEmptyTile ? 'cursor-pointer' : '',
          ].join(' ')}
        >
          <button
            type="button"
            aria-label={`Decrease ${label} quantity`}
            onClick={event => {
              event.stopPropagation()
              onDecrement()
            }}
            disabled={quantity === 0}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-lg leading-none text-gray-900 transition hover:border-gray-500 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-vi-red focus-visible:ring-offset-2"
          >
            -
          </button>
          <span className="min-w-8 text-center tabular-nums" aria-live="polite">
            {quantity}
          </span>
          <button
            type="button"
            aria-label={`Increase ${label} quantity`}
            onClick={event => {
              event.stopPropagation()
              onIncrement()
            }}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-gray-300 bg-white text-lg leading-none text-gray-900 transition hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-vi-red focus-visible:ring-offset-2"
          >
            +
          </button>
        </div>
      )}
    </div>
  )
}

export function HybridCupAddToCart({
  flowSlug,
  searchParams,
}: CustomAddToCartFlowProps) {
  const router = useRouter()
  const flowMeta = getCustomAddToCartFlowMetadata(flowSlug)
  const storefrontMarket = useStorefrontMarket()
  const brandCode = storefrontMarket.brandCode ?? 'VANISHING_INC_US'
  const productId = getProductIdFromSearchParams(searchParams)

  const [step, setStep] = useState<StepIndex>(STEP.CUP)
  const prevStepRef = useRef<StepIndex | null>(null)
  useLayoutEffect(() => {
    if (prevStepRef.current !== null && prevStepRef.current !== step) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
    prevStepRef.current = step
  }, [step])
  const [selectedCupOptionId, setSelectedCupOptionId] = useState<number | null>(
    null
  )
  const [selectedSleeveQuantities, setSelectedSleeveQuantities] = useState<
    Record<number, number>
  >({})
  const [includeSolidCup, setIncludeSolidCup] = useState(false)
  const [addProgress, setAddProgress] = useState<AddProgress | null>(null)

  const cupProductQuery = useProductByIdQuery(
    { id: productId ?? 0, brandCode },
    { enabled: Boolean(productId) }
  )
  const sleevesProductQuery = useProductByIdQuery(
    { id: SLEEVES_PRODUCT_ID, brandCode },
    { enabled: true }
  )
  const solidCupProductQuery = useProductByIdQuery(
    { id: SOLID_CUP_PRODUCT_ID, brandCode },
    { enabled: true }
  )

  const cupProduct = cupProductQuery.data?.ecommProductById
  const sleevesProduct = sleevesProductQuery.data?.ecommProductById
  const solidCupProduct = solidCupProductQuery.data?.ecommProductById

  const allCupOptions = useMemo(
    () => (cupProduct?.options ?? []) as Option[],
    [cupProduct?.options]
  )
  const cupOptions = useMemo(
    () => allCupOptions.filter(isSelectableOption),
    [allCupOptions]
  )
  const allSleeveOptions = useMemo(
    () => (sleevesProduct?.options ?? []) as Option[],
    [sleevesProduct?.options]
  )
  const sleeveOptions = useMemo(
    () => allSleeveOptions.filter(isSelectableOption),
    [allSleeveOptions]
  )
  const selectedCupOptionIsInStock =
    selectedCupOptionId != null &&
    cupOptions.some(option => option.id === selectedCupOptionId)
  const selectedCupOption = useMemo(
    () => cupOptions.find(option => option.id === selectedCupOptionId),
    [cupOptions, selectedCupOptionId]
  )
  const selectedCupIsStainlessSteel =
    selectedCupOption?.description?.toLowerCase().includes('stainless steel') ??
    false
  const includedSleeveCount = selectedCupOption?.description
    ?.toLowerCase()
    .includes('set of 3 cups')
    ? 3
    : 1
  const selectedSleeveUnitOptionIds = useMemo(
    () =>
      sleeveOptions.flatMap(option => {
        const quantity = selectedSleeveQuantities[option.id] ?? 0
        return Array.from({ length: quantity }, () => option.id)
      }),
    [selectedSleeveQuantities, sleeveOptions]
  )
  const selectedSleeveCount = selectedSleeveUnitOptionIds.length

  const needsCupPick = allCupOptions.length > 0
  const currencySymbol =
    cupOptions[0]?.currency?.symbol ??
    sleeveOptions[0]?.currency?.symbol ??
    allCupOptions[0]?.currency?.symbol ??
    allSleeveOptions[0]?.currency?.symbol ??
    '$'

  const extraSleeveUnitPrice = useMemo(() => {
    const ref = allSleeveOptions[0]
    if (!ref) return null
    return effectiveOptionPrice(ref)
  }, [allSleeveOptions])

  const pricingReady =
    extraSleeveUnitPrice != null &&
    Number.isFinite(extraSleeveUnitPrice) &&
    extraSleeveUnitPrice > 0 &&
    allSleeveOptions.length > 0

  const { addItem, isPending } = useAddItemToCart({
    onError: err => {
      toastEmitter.emit('toast', {
        title: err instanceof Error ? err.message : 'Could not add to cart',
        variant: 'error',
      })
    },
  })

  const incrementSleeveQuantity = useCallback((optionId: number) => {
    setSelectedSleeveQuantities(prev => ({
      ...prev,
      [optionId]: (prev[optionId] ?? 0) + 1,
    }))
  }, [])

  const decrementSleeveQuantity = useCallback((optionId: number) => {
    setSelectedSleeveQuantities(prev => {
      const nextQuantity = (prev[optionId] ?? 0) - 1
      const next = { ...prev }
      if (nextQuantity <= 0) {
        delete next[optionId]
      } else {
        next[optionId] = nextQuantity
      }
      return next
    })
  }, [])

  const goNextFromCup = useCallback(() => {
    if (needsCupPick && !selectedCupOptionIsInStock) {
      toastEmitter.emit('toast', {
        title: 'Please select a finish for your Hybrid Cup.',
        variant: 'error',
      })
      return
    }
    setStep(STEP.SLEEVES)
  }, [needsCupPick, selectedCupOptionIsInStock])

  const goNextFromSleeves = useCallback(() => {
    if (selectedSleeveCount === 0) {
      toastEmitter.emit('toast', {
        title: 'Please select at least one sleeve.',
        variant: 'error',
      })
      return
    }
    if (
      includedSleeveCount === 3 &&
      selectedSleeveCount < includedSleeveCount
    ) {
      const remainingSleeves = includedSleeveCount - selectedSleeveCount
      const shouldContinue = window.confirm(
        `Your cup set includes ${remainingSleeves} more free ${remainingSleeves === 1 ? 'sleeve' : 'sleeves'}. Would you like to continue without ${remainingSleeves === 1 ? 'it' : 'them'}?`
      )
      if (!shouldContinue) return
    }
    setStep(STEP.EXTRA)
  }, [includedSleeveCount, selectedSleeveCount])

  const goBack = useCallback(() => {
    setStep(s => Math.max(STEP.CUP, s - 1) as StepIndex)
  }, [])

  const handleAddToCart = useCallback(async () => {
    if (addProgress) return
    if (!cupProduct?.id || !productId) return
    if (needsCupPick && !selectedCupOptionIsInStock) {
      toastEmitter.emit('toast', {
        title: 'Please choose a finish for your Hybrid Cup.',
        variant: 'error',
      })
      return
    }
    if (selectedSleeveCount === 0) {
      toastEmitter.emit('toast', {
        title: 'Please select at least one sleeve.',
        variant: 'error',
      })
      return
    }
    const addSolidCup =
      selectedCupIsStainlessSteel && includeSolidCup && solidCupProduct?.id
    const totalItems = 1 + selectedSleeveCount + (addSolidCup ? 1 : 0)
    let currentItem = 1

    try {
      setAddProgress({
        current: currentItem,
        total: totalItems,
        label: 'Adding your Hybrid Cup',
      })
      await addItem({
        productId: cupProduct.id,
        productOptionId: selectedCupOptionId ?? undefined,
        quantity: 1,
      })

      for (const [
        index,
        sleeveOptionId,
      ] of selectedSleeveUnitOptionIds.entries()) {
        currentItem += 1
        setAddProgress({
          current: currentItem,
          total: totalItems,
          label: `Adding sleeve ${index + 1} of ${selectedSleeveCount}`,
        })
        const description =
          index < includedSleeveCount
            ? HYBRID_CUP_INCLUDED_SLEEVE_DESCRIPTION
            : undefined
        await addItem({
          productId: SLEEVES_PRODUCT_ID,
          productOptionId: sleeveOptionId,
          description,
          quantity: 1,
        })
      }

      if (addSolidCup) {
        currentItem += 1
        setAddProgress({
          current: currentItem,
          total: totalItems,
          label: 'Adding the solid cup',
        })
        await addItem({
          productId: addSolidCup,
          quantity: 1,
        })
      }

      setAddProgress({
        current: totalItems,
        total: totalItems,
        label: 'Opening checkout',
      })
      router.push('/checkout')
    } catch {
      // surfaced via onError
      setAddProgress(null)
    }
  }, [
    addProgress,
    cupProduct,
    productId,
    needsCupPick,
    selectedCupOptionId,
    selectedCupOptionIsInStock,
    selectedCupIsStainlessSteel,
    includedSleeveCount,
    selectedSleeveCount,
    selectedSleeveUnitOptionIds,
    includeSolidCup,
    solidCupProduct,
    addItem,
    router,
  ])

  if (productId == null) {
    return (
      <div className="w-full min-h-[50vh] flex items-center justify-center px-4">
        <p className="text-gray-600 text-center">
          Missing product. Open this page from the product with{' '}
          <code className="text-sm">?productId=…</code>.
        </p>
      </div>
    )
  }

  if (
    cupProductQuery.isLoading ||
    sleevesProductQuery.isLoading ||
    !cupProduct ||
    !sleevesProduct
  ) {
    return (
      <div className="w-full min-h-[50vh] flex flex-col">
        <div className="h-40 md:h-48 bg-vi-gray animate-pulse px-4 py-10 md:py-14">
          <div className="max-w-(--breakpoint-xl) mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              {flowMeta?.title ?? 'Configure your Hybrid Cup'}
            </h1>
          </div>
        </div>
        <div className="flex-1 flex justify-center items-center py-16">
          <div className="rounded-lg bg-gray-200 h-12 w-64 animate-pulse" />
        </div>
      </div>
    )
  }

  if (!pricingReady) {
    return (
      <div className="w-full min-h-[50vh] flex flex-col items-center justify-center px-4 bg-gray-100 py-12">
        <div className="max-w-md w-full text-center">
          <BackToProductLink productUrl={cupProduct.url} variant="muted" />
          <p className="text-red-700 text-lg leading-relaxed">
            Sleeve pricing is missing or incomplete in the catalogue.
            Configuration cannot continue.
          </p>
        </div>
      </div>
    )
  }

  const extraSleeve = extraSleeveUnitPrice
  const flowTitle = flowMeta?.title ?? 'Configure your Hybrid Cup'
  const flowDescription =
    flowMeta?.description ??
    'Choose your cup finish, sleeves, and optional solid cup add-on.'
  const headerSubtitle = flowDescription
    ? `${flowTitle} — ${flowDescription}`
    : flowTitle

  const solidCupLoading =
    selectedCupIsStainlessSteel && solidCupProductQuery.isLoading
  const solidCupAvailable =
    selectedCupIsStainlessSteel && Boolean(solidCupProduct?.id)
  const solidDisplayPrice = solidCupProduct?.displayPrice ?? 0
  const showSolidCupOffer = solidCupLoading || solidCupAvailable

  return (
    <div className="flex w-full min-h-[min(100dvh,1200px)] flex-col bg-gray-100">
      <header className="bg-vi-gray text-white py-10 md:py-14 shrink-0 shadow-md w-full">
        <div className="max-w-(--breakpoint-xl) mx-auto px-4 xl:px-0">
          <BackToProductLink productUrl={cupProduct.url} variant="header" />
          <p className="text-gray-300 text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-2">
            Vorst &amp; Bosch
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            {cupProduct.name}
          </h1>
          <p className="mt-3 text-base md:text-lg text-white/88 max-w-3xl leading-relaxed">
            {headerSubtitle}
          </p>
        </div>
      </header>

      <main className="flex-1 w-full max-w-(--breakpoint-xl) mx-auto px-4 xl:px-0 py-8 md:py-12">
        {step === STEP.CUP && (
          <section className="bg-white p-6 md:p-10 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-8">
              Step 1: Choose your cup finish
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-10 max-w-4xl">
              {allCupOptions.map(opt => (
                <ConfiguratorTile
                  key={opt.id ?? opt.description}
                  label={opt.description ?? 'Option'}
                  imageUrl={hybridCupCupSwatchUrl(opt.description)}
                  selected={opt.inStock && selectedCupOptionId === opt.id}
                  currencySymbol={opt.currency?.symbol ?? currencySymbol}
                  price={opt.price}
                  salePrice={opt.salePrice}
                  imageSize="88%"
                  disabled={!opt.inStock || opt.id == null}
                  onSelect={() => {
                    if (!opt.inStock) return
                    setSelectedCupOptionId(opt.id ?? null)
                    if (
                      !opt.description
                        ?.toLowerCase()
                        .includes('stainless steel')
                    ) {
                      setIncludeSolidCup(false)
                    }
                  }}
                />
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                color="green"
                onClick={goNextFromCup}
                disabled={needsCupPick && !selectedCupOptionIsInStock}
              >
                Next: choose sleeves
              </Button>
            </div>
          </section>
        )}

        {step === STEP.SLEEVES && (
          <section className="bg-white p-6 md:p-10 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
              Step 2: Choose your sleeves
            </h2>
            <p className="text-gray-700 mb-8 leading-relaxed">
              {includedSleeveCount === 3
                ? 'Your first three sleeves are included with this cup set'
                : 'Your first sleeve is included with the cup'}
              ; each additional sleeve is {currencySymbol}
              {Number(extraSleeve).toFixed(2)}. Select any combination — tap
              again to remove.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 mb-10">
              {allSleeveOptions.map(opt => (
                <SleeveQuantityTile
                  key={opt.id ?? opt.description}
                  label={opt.description ?? 'Sleeve'}
                  imageUrl={hybridCupSleeveSwatchUrl(opt.description)}
                  quantity={
                    opt.inStock && opt.id != null
                      ? (selectedSleeveQuantities[opt.id] ?? 0)
                      : 0
                  }
                  disabled={!opt.inStock || opt.id == null}
                  onIncrement={() =>
                    opt.inStock &&
                    opt.id != null &&
                    incrementSleeveQuantity(opt.id)
                  }
                  onDecrement={() =>
                    opt.inStock &&
                    opt.id != null &&
                    decrementSleeveQuantity(opt.id)
                  }
                />
              ))}
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-4">
              <span className="text-sm text-gray-500">
                {selectedSleeveCount === 0
                  ? 'No sleeves selected'
                  : `${selectedSleeveCount} sleeve${selectedSleeveCount === 1 ? '' : 's'} selected`}
              </span>
              <div className="flex gap-2 justify-end">
                <Button color="gray" onClick={goBack}>
                  Back
                </Button>
                <Button color="green" onClick={goNextFromSleeves}>
                  {showSolidCupOffer
                    ? 'Next: optional solid cup'
                    : 'Next: review selection'}
                </Button>
              </div>
            </div>
          </section>
        )}

        {step === STEP.EXTRA && (
          <section className="bg-white p-6 md:p-10 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
              {showSolidCupOffer
                ? 'Step 3: Solid cup (optional, stainless steel)'
                : 'Step 3: Review your selection'}
            </h2>
            <div className="text-gray-700 border-b border-gray-200 pb-6 mb-6 space-y-3 leading-relaxed">
              {showSolidCupOffer ? (
                <>
                  <p>
                    Want the strongest possible finish? Add the solid cup — it
                    is the perfect way to end your routine: switch out the
                    Hybrid Cup for a completely solid, heavy cup and let the
                    moment land.
                  </p>
                  <p className="text-sm text-gray-600">
                    Optional. Leave it unchecked to order only the Hybrid Cup
                    and your sleeves. The solid cup add-on is available only in
                    stainless steel.
                  </p>
                </>
              ) : (
                <p>
                  Review your Hybrid Cup and sleeve selections before adding
                  them to your cart.
                </p>
              )}
            </div>

            {solidCupLoading ? (
              <div className="h-40 bg-gray-100 animate-pulse rounded-lg mb-8" />
            ) : solidCupAvailable ? (
              <label className="flex flex-col sm:flex-row rounded-lg border border-gray-200 overflow-hidden hover:border-gray-300 cursor-pointer transition mb-8 text-left">
                <div className="relative w-full sm:w-48 md:w-56 shrink-0 aspect-square sm:aspect-auto sm:min-h-[168px] bg-white border-b sm:border-b-0 sm:border-r border-gray-200">
                  <img
                    src={hybridCupSolidCupPromoImageUrl()}
                    alt={solidCupProduct?.name ?? 'Solid cup'}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-1 items-start gap-3 p-4 min-w-0">
                  <input
                    type="checkbox"
                    checked={includeSolidCup}
                    onChange={() => setIncludeSolidCup(v => !v)}
                    className="mt-1 rounded border-gray-300 text-vi-red focus:ring-vi-red shrink-0"
                  />
                  <div className="min-w-0">
                    <span className="font-semibold text-gray-900 block">
                      {solidCupProduct?.name ?? 'Solid cup'}
                    </span>
                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                      Switch: unmistakably solid and heavy in the hands.
                      Available only in stainless steel.
                    </p>
                    <span className="text-sm text-gray-900 mt-3 block font-medium tabular-nums">
                      <Price
                        price={solidDisplayPrice}
                        currencySymbol={
                          solidCupProduct?.currencySymbol ?? currencySymbol
                        }
                        size="sm"
                      />
                    </span>
                  </div>
                </div>
              </label>
            ) : null}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
              <div className="text-sm text-gray-500 space-y-0.5">
                <div>
                  {selectedSleeveCount}{' '}
                  {selectedSleeveCount === 1 ? 'sleeve' : 'sleeves'} (
                  {Math.min(selectedSleeveCount, includedSleeveCount)} included
                  with your Hybrid Cup)
                </div>
                {includeSolidCup && solidCupAvailable ? (
                  <div>Includes {solidCupProduct?.name ?? 'Solid cup'}</div>
                ) : null}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  color="gray"
                  onClick={goBack}
                  disabled={addProgress !== null}
                >
                  Back
                </Button>
                <Button
                  color="green"
                  onClick={handleAddToCart}
                  disabled={isPending || addProgress !== null}
                >
                  {addProgress
                    ? `Adding item ${addProgress.current} of ${addProgress.total}…`
                    : isPending
                      ? 'Adding…'
                      : 'Add to cart'}
                </Button>
              </div>
            </div>
            {addProgress ? (
              <div
                className="mt-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-center justify-between gap-4 font-semibold">
                  <span>{addProgress.label}…</span>
                  <span className="tabular-nums">
                    {addProgress.current}/{addProgress.total}
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-blue-100">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-[width] duration-300"
                    style={{
                      width: `${(addProgress.current / addProgress.total) * 100}%`,
                    }}
                  />
                </div>
                <p className="mt-2 text-blue-800">
                  This may take a moment. Please keep this page open while we
                  prepare your cart.
                </p>
              </div>
            ) : null}
          </section>
        )}
      </main>
    </div>
  )
}
